<template>
	<div class="left-panel">
		<!-- 搜索和筛选行 -->
		<div class="search-filter-row">
			<!-- 搜索框 -->
			<div class="search-container">
				<input
					v-model="searchInput"
					@input="filterMarketList"
					class="search-input"
					type="text"
					:placeholder="$t('search.SearchHere') || '搜索'"
				/>
				<div class="search-icon">
					<i class="el-icon-search"></i>
				</div>
			</div>
			
			<!-- 分类下拉选择框 -->
			<el-select 
				v-model="selectedCategoryIndex" 
				@change="filterMarketList"
				class="category-select"
				:placeholder="$t('Market.all') || '请选择'"
			>
				<el-option
					:label="$t('Market.all') || 'All'"
					:value="-1"
				></el-option>
				<el-option
					v-for="(item, index) in getMarkTypeList"
					:key="index"
					:label="item.label"
					:value="index"
				></el-option>
			</el-select>
		</div>
		
		<div class="stock-list u-list">
			<div
				class="u-list-item"
				v-for="(stock, index) in (filteredMarketList.length > 0 ? filteredMarketList : marketList)"
				:key="index"
				@click="handleSelectStock(stock)"
			>
				<div class="u-cell"
					:class="{ 
						'active': selectedStock && selectedStock.code === stock.code,
						'price-flash': priceFlashMap[stock.code]
					}">
					<div class="u-avatar">
						<img :src="stock.logo"
							:alt="stock.code"
							class="avatar-img" />
					</div>
					<div class="u-cell-title">
						<p class="u-cell-text stock-name">{{ stock.appName || stock.show_name || stock.code }}</p>
						<!-- 当前价格显示 -->
						<p class="u-cell-text stock-price-section">
							<span v-if="getColorType(stock.zdf || stock.change) == 12" class="price-text price-up">
								{{ formatPrice(stock.nowPrice || stock.price) }}
							</span>
							<span v-if="getColorType(stock.zdf || stock.change) == 13" class="price-text price-down">
								{{ formatPrice(stock.nowPrice || stock.price) }}
							</span>
							<span v-if="getColorType(stock.zdf || stock.change) == 14" class="price-text price-neutral">
								{{ formatPrice(stock.nowPrice || stock.price) }}
							</span>
						</p>
					<!-- 涨跌幅显示 -->
					<div class="change-section">
						<!-- 根据交易状态API显示休市 -->
						<div v-if="getTradingStatus(stock.id).canTrade === false" class="market-closed-tag">
							{{ $t('Market.closed') || '休市' }}
						</div>
						<!-- 显示涨跌幅 -->
						<div v-else-if="getTradingStatus(stock.id).canTrade === true || getTradingStatus(stock.id).canTrade === null">
							<div v-if="getColorType(stock.zdf || stock.change) == 13" class="change-tag change-down">
								-{{ formatChange(stock.zdf || stock.change) }}%
							</div>
							<div v-if="getColorType(stock.zdf || stock.change) == 12" class="change-tag change-up">
								+{{ formatChange(stock.zdf || stock.change) }}%
							</div>
							<div v-if="getColorType(stock.zdf || stock.change) == 14" class="change-tag change-neutral">
								{{ formatChange(stock.zdf || stock.change) }}%
							</div>
						</div>
					</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'MarketList',
	props: {
		marketList: {
			type: Array,
			default: () => []
		},
		selectedStock: {
			type: Object,
			default: null
		},
		getMarkTypeList: {
			type: Array,
			default: () => []
		},
		tradingStatuses: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			// 搜索和筛选
			searchInput: '',
			selectedCategoryIndex: -1, // -1表示全部，其他为分类索引
			filteredMarketList: [],
			// 价格跳动效果
			priceFlashMap: {}, // 存储每个币种的价格闪烁状态 {code: true/false}
			lastPriceMap: {} // 存储每个币种的上一次价格 {code: price}
		};
	},
	watch: {
		// 监听市场列表变化，检测价格变动
		marketList: {
			handler(newList) {
				if (!newList || newList.length === 0) return;
				
				newList.forEach(stock => {
					const code = stock.code;
					const currentPrice = stock.nowPrice || stock.price;
					const lastPrice = this.lastPriceMap[code];
					
					// 如果价格发生变化（且不是第一次加载）
					if (lastPrice !== undefined && currentPrice !== lastPrice && currentPrice !== undefined) {
						// 触发闪烁效果
						this.$set(this.priceFlashMap, code, true);
						
						// 1500ms后移除闪烁效果（1.5秒，让用户有足够时间注意到）
						setTimeout(() => {
							this.$set(this.priceFlashMap, code, false);
						}, 1500);
					}
					
					// 更新最后价格
					if (currentPrice !== undefined) {
						this.$set(this.lastPriceMap, code, currentPrice);
					}
				});
			},
			deep: true
		}
	},
	methods: {
		// 搜索和筛选方法
		filterMarketList() {
			let filteredList = [...this.marketList];
			
			// 1. 先按分类筛选
			if (this.selectedCategoryIndex >= 0 && this.getMarkTypeList[this.selectedCategoryIndex]) {
				const selectedType = this.getMarkTypeList[this.selectedCategoryIndex].value;
				filteredList = filteredList.filter(item => {
					const itemType = String(item.type || item.marketType || '');
					return itemType === selectedType;
				});
			}
			
		// 2. 再按搜索关键字筛选（优先精确匹配，再模糊匹配）
		if (this.searchInput.trim()) {
			const searchLower = this.searchInput.toLowerCase().trim();
			
			// 分别收集精确匹配和模糊匹配的结果
			const exactMatches = [];
			const fuzzyMatches = [];
			
			filteredList.forEach(item => {
				const code = (item.code || '').toLowerCase();
				const showName = (item.show_name || '').toLowerCase();
				const appName = (item.appName || '').toLowerCase();
				
				// 精确匹配：完全相等
				if (code === searchLower || showName === searchLower || appName === searchLower) {
					exactMatches.push(item);
				}
				// 模糊匹配：包含关键字
				else if (code.includes(searchLower) || showName.includes(searchLower) || appName.includes(searchLower)) {
					fuzzyMatches.push(item);
				}
			});
			
			// 精确匹配的结果排在前面
			filteredList = [...exactMatches, ...fuzzyMatches];
		}

		this.filteredMarketList = filteredList;
		},
		
		// 选择币种
		handleSelectStock(stock) {
			this.$emit('select-stock', stock);
		},
		
		// 获取颜色类型
		getColorType(change) {
			const changeValue = parseFloat(change) || 0;
			if (changeValue > 0) return 12; // 上涨 - 蓝色
			if (changeValue < 0) return 13; // 下跌 - 红色
			return 14; // 平盘 - 灰色
		},
		
		// 格式化价格显示 - 原模原样显示WebSocket推送的价格
		formatPrice(price) {
			if (!price && price !== 0) return '--';
			const priceValue = parseFloat(price);
			if (isNaN(priceValue)) return '--';
			
			// 直接返回原始价格字符串，不做任何格式化
			// 如果价格是整数，则不显示小数点
			if (Number.isInteger(priceValue)) {
				return priceValue.toString();
			}
			
			// 返回原始价格，保留所有小数位
			return price.toString();
		},
		
	// 格式化涨跌幅显示
	formatChange(change) {
		if (!change && change !== 0) return '0.00';
		const changeValue = parseFloat(change);
		if (isNaN(changeValue)) return '0.00';
		return Math.abs(changeValue).toFixed(2);
	},
	
	// 获取交易状态
	getTradingStatus(marketId) {
		const status = this.tradingStatuses[marketId]
		if (!status) {
			return { canTrade: null, reason: '检查中...' }
		}
		return status
	}
}
};
</script>

<style scoped>
/* 左侧股票列表 - 完全对标st5-pc */
.left-panel {
	width: 350px;
	height: 770px;
	background: var(--card-bg);
	border-radius: 12px;
	overflow-y: auto;
	overflow-x: hidden;
	scrollbar-width: none; /* Firefox */
	-ms-overflow-style: none; /* IE and Edge */
	transition: background-color 0.3s ease;
	margin-left: 12px;
	margin-top: 18px;
	position: relative;
	z-index: 1;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
	border: 1px solid rgba(0, 0, 0, 0.02);
}

.left-panel::-webkit-scrollbar {
	display: none; /* Chrome, Safari and Opera */
}

/* 搜索和筛选行 */
.search-filter-row {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 16px 16px;
	background: var(--card-bg);
	position: sticky;
	top: 0;
	z-index: 2;
	transition: background-color 0.3s ease;
	border-radius: 12px 12px 0 0;
}

/* 搜索框容器 */
.search-container {
	position: relative;
	flex: 1;
	min-width: 0;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: var(--text-tertiary, #999);
	font-size: 16px;
	z-index: 1;
	opacity: 0.6;
	transition: opacity 0.2s, color 0.2s;
	pointer-events: none;
}

.search-input {
	width: 100%;
	padding: 10px 12px 10px 38px;
	border: 1px solid var(--border-light, transparent);
	border-radius: 4px;
	font-size: 13px;
	outline: none;
	background: var(--bg-secondary, #f5f5f5);
	color: var(--text-primary);
	transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
	box-shadow: none;
}

.search-input:hover {
	background: var(--bg-tertiary, #ececec);
	border-color: var(--border-light, transparent);
}

.search-input:focus {
	background: var(--card-bg, #fff);
	border-color: var(--border-focus, rgba(0, 0, 0, 0.05));
	box-shadow: none;
}

.search-input:focus ~ .search-icon {
	opacity: 0.8;
	color: var(--text-secondary);
}

.search-input::placeholder {
	color: var(--text-tertiary, #999);
	font-size: 13px;
	opacity: 0.8;
}

/* 分类选择器 */
.category-select {
	width: 120px;
	flex-shrink: 0;
}

/* Element UI Select 组件黑夜模式适配 */
.category-select >>> .el-input__inner {
	background-color: var(--bg-secondary, #f5f5f5) !important;
	border: 1px solid var(--border-light, transparent) !important;
	border-radius: 4px !important;
	color: var(--text-primary, #333) !important;
	padding: 10px 30px 10px 12px !important;
	font-size: 13px !important;
	height: auto !important;
	line-height: 1.4 !important;
	transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease !important;
	white-space: nowrap !important;
	overflow: hidden !important;
	text-overflow: ellipsis !important;
	box-shadow: none !important;
}

.category-select >>> .el-input__inner:hover {
	background-color: var(--bg-tertiary, #ececec) !important;
	border-color: var(--border-light, transparent) !important;
}

.category-select >>> .el-input__inner:focus {
	background-color: var(--card-bg, #fff) !important;
	border-color: var(--border-focus, rgba(0, 0, 0, 0.05)) !important;
	box-shadow: none !important;
}

.category-select >>> .el-input__icon {
	color: var(--text-tertiary, #999) !important;
	transition: color 0.3s ease;
	font-size: 14px !important;
}

.category-select >>> .el-input__suffix {
	color: var(--text-tertiary, #999) !important;
	right: 12px !important;
	display: flex !important;
	align-items: center !important;
	top: 50% !important;
	transform: translateY(-50%) !important;
}

.category-select >>> .el-icon-arrow-up {
	font-size: 12px !important;
	line-height: 1 !important;
}

.category-select >>> .el-select__caret {
	color: var(--text-secondary, #999) !important;
	font-size: 11px !important;
}

/* 币种列表 */
.stock-list {
	width: 100%;
	overflow: hidden;
}

.u-list {
	width: 100%;
}

.u-list-item {
	cursor: pointer;
	transition: background-color 0.2s;
	margin: 3px 0; /* 上下间距（稍微有点间隙，不会太宽） */
}

.u-list-item:first-child {
	margin-top: 0; /* 第一个不需要上边距 */
}

.u-list-item:last-child {
	margin-bottom: 0; /* 最后一个不需要下边距 */
}

.u-list-item:hover .u-cell {
	background: var(--hover-bg, rgba(1, 102, 252, 0.05));
	border-radius: 8px; /* 悬停时圆角 */
}

.u-cell {
	display: flex;
	align-items: center;
	padding: 12px 16px;
	padding-left: 20px; /* ensure space at the left so logos are not clipped */
	gap: 12px;
	transition: all 0.2s;
}

/* 选中状态（已禁用背景色和左边竖线） */
/* .u-cell.active {
	background: var(--active-bg, rgba(1, 102, 252, 0.1));
	border-left: 3px solid var(--primary-color);
} */

/* 价格变动闪烁效果 */
.u-cell.price-flash {
	animation: priceFlash 1.5s ease-out;
	border-radius: 8px; /* 闪烁时的圆角 */
}

@keyframes priceFlash {
	0% {
		background-color: transparent;
		box-shadow: 0 0 0 0 var(--primary-color-20, rgba(1, 86, 220, 0.2));
	}
	30% {
		background-color: var(--primary-color-10, rgba(1, 86, 220, 0.1));
		box-shadow: 0 0 10px 0 var(--primary-color-15, rgba(1, 86, 220, 0.15));
	}
	70% {
		background-color: var(--primary-color-10, rgba(1, 86, 220, 0.1));
		box-shadow: 0 0 10px 0 var(--primary-color-15, rgba(1, 86, 220, 0.15));
	}
	100% {
		background-color: transparent;
		box-shadow: 0 0 0 0 var(--primary-color-20, rgba(1, 86, 220, 0.2));
	}
}

.u-avatar {
	flex-shrink: 0;
	width: 48px;
	height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: visible;
}

.avatar-img {
	width: 40px;
	height: 40px;
	object-fit: contain;
}

.u-cell-title {
	flex: 1;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 4px;
}

.u-cell-text {
	margin: 0;
	font-size: 14px;
}

.stock-name {
	font-weight: 600;
	color: var(--text-primary);
	flex: 0 0 auto;
	max-width: 100px;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.stock-price-section {
	flex-shrink: 0;
	text-align: right;
	margin-left: 8px;
	margin-right: 16px;
}

.price-text {
	font-weight: 600;
	font-size: 14px;
}

.price-up {
	color: var(--primary-color, #0156DC);
}

.price-down {
	color: #FF4B4B;
}

.price-neutral {
	color: var(--text-secondary);
}

.change-section {
	flex-shrink: 0;
	text-align: right;
}

.change-tag {
	display: inline-block;
	padding: 8px 12px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 600;
	min-width: 65px;
	text-align: center;
}

.change-up {
	background: var(--primary-color, #0156DC);
	color: #ffffff;
}

.change-down {
	background: #FF4B4B;
	color: #ffffff;
}

.change-neutral {
	background: var(--bg-secondary);
	color: var(--text-secondary);
}

.market-closed-tag {
	display: inline-block;
	padding: 4px 8px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 600;
	background: var(--bg-secondary);
	color: var(--text-secondary);
	min-width: 60px;
	text-align: center;
}
</style>

<!-- 下拉菜单全局样式（非 scoped，用于 el-select-dropdown） -->
<style>
/* Element UI Select Dropdown 黑夜模式适配 */
.el-select-dropdown {
	background-color: var(--card-bg, #ffffff) !important;
	border: 1px solid rgba(0, 0, 0, 0.03) !important;
	box-shadow: var(--dropdown-shadow, 0 2px 12px 0 rgba(0, 0, 0, 0.03)) !important;
	transition: background-color 0.3s ease, border-color 0.3s ease;
}

.el-select-dropdown__item {
	background-color: var(--card-bg, #ffffff) !important;
	color: var(--text-primary, #333) !important;
	transition: background-color 0.3s ease, color 0.3s ease;
}

.el-select-dropdown__item:hover {
	background-color: var(--hover-bg, rgba(1, 102, 252, 0.1)) !important;
}

.el-select-dropdown__item.selected {
	background-color: var(--active-bg, rgba(1, 102, 252, 0.15)) !important;
	color: var(--primary-color, #0156DC) !important;
	font-weight: 600;
}

.el-select-dropdown__item.hover {
	background-color: var(--hover-bg, rgba(1, 102, 252, 0.1)) !important;
}

.el-popper[x-placement^=bottom] .popper__arrow {
	border-bottom-color: var(--border-color, #dcdfe6) !important;
}

.el-popper[x-placement^=bottom] .popper__arrow::after {
	border-bottom-color: var(--card-bg, #ffffff) !important;
}

.el-popper[x-placement^=top] .popper__arrow {
	border-top-color: var(--border-color, #dcdfe6) !important;
}

.el-popper[x-placement^=top] .popper__arrow::after {
	border-top-color: var(--card-bg, #ffffff) !important;
}
</style>
